/**
 * AuraVeil Audio Logo — 528Hz Solfeggio "Love Frequency" chime.
 *
 * Rendered live with the Web Audio API (no external asset). A 3-second
 * resonant tone built from the 528Hz fundamental plus a fifth (792Hz),
 * an octave (1056Hz), and a subharmonic (264Hz) to evoke a Tibetan
 * singing bowl's warmth. Soft attack (~150ms) and long exponential
 * decay so it feels like an "energetic alignment," not a notification.
 */

let ctxRef = null;
let unlocked = false;

function getCtx() {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!ctxRef) ctxRef = new AC();
  return ctxRef;
}

/** Some browsers suspend the context until a user gesture. Call from a click. */
export function unlockAudioLogo() {
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }
  unlocked = true;
}

export function playAudioLogo({ muted = false, volume = 0.22 } = {}) {
  if (muted) return;
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") {
    // Attempt resume; if blocked (no gesture yet), silently no-op.
    ctx.resume().catch(() => {});
    if (ctx.state === "suspended") return;
  }
  unlocked = true;

  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.setValueAtTime(0.0001, now);
  master.gain.linearRampToValueAtTime(volume, now + 0.15); // gentle attack
  master.gain.exponentialRampToValueAtTime(0.0001, now + 3.0); // long decay
  master.connect(ctx.destination);

  // A subtle low-pass for warmth (bowl-like, not glassy)
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(2400, now);
  filter.Q.setValueAtTime(0.7, now);
  filter.connect(master);

  const partials = [
    { freq: 528, gain: 1.0, type: "sine" }, // fundamental — 528Hz Solfeggio
    { freq: 792, gain: 0.45, type: "sine" }, // perfect fifth (528 * 1.5)
    { freq: 1056, gain: 0.2, type: "sine" }, // octave (528 * 2)
    { freq: 264, gain: 0.35, type: "sine" }, // subharmonic (528 / 2) for warmth
  ];

  partials.forEach((p) => {
    const osc = ctx.createOscillator();
    osc.type = p.type;
    osc.frequency.setValueAtTime(p.freq, now);
    // A hair of detune drift for organic shimmer
    osc.detune.setValueAtTime(0, now);
    osc.detune.linearRampToValueAtTime(6, now + 3.0);
    const g = ctx.createGain();
    g.gain.setValueAtTime(p.gain, now);
    osc.connect(g).connect(filter);
    osc.start(now);
    osc.stop(now + 3.2);
  });
}

export function isAudioLogoUnlocked() {
  return unlocked;
}
